import { useEffect, useMemo, type ComponentProps } from 'react'
import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { paraArray } from '../../dominio/geometria/vetor3'
import { Barra, orientacaoEntre } from '../componentes/Barra'
import { cortarExtremos, planejarTrecho } from '../componentes/emendaDeBarras'
import { MaterialPeca } from '../componentes/Peca'

/** Espessura da tampa que fecha um extremo livre da seção oca. */
export const espessuraDaTampa = (largura: number, espessura: number) => Math.min(largura, espessura) * 0.065 * 1.5

/**
 * Seção oca ilustrativa, com abas e soldas; não é uma seção de fabricante.
 *
 * Os extremos ficam abertos, para a seção aparecer vazada num corte. Um extremo
 * LIVRE de uma coluna (o pé e o topo, onde ela não encosta em outro trecho dela)
 * pode ser fechado com `tampaInicio` / `tampaFim`, para não mostrar um tubo aberto.
 */
export function geometriaChapaEstrutural(
  largura: number,
  espessura: number,
  tamanho: number,
  { tampaInicio = false, tampaFim = false }: { tampaInicio?: boolean; tampaFim?: boolean } = {},
) {
  const chapas: THREE.BufferGeometry[] = []
  const parede = Math.min(largura, espessura) * 0.065
  const aba = largura * 0.18
  const caixa = (w: number, h: number, d: number, x: number, y: number, z: number) => {
    chapas.push(new THREE.BoxGeometry(w, h, d).translate(x, y, z))
  }
  // Quatro paredes deixam o interior e os extremos abertos. Não há bloco maciço.
  for (const lado of [-1, 1]) {
    caixa(parede, tamanho, espessura, lado * (largura - parede) / 2, 0, 0)
    caixa(largura - parede * 2, tamanho, parede, 0, 0, lado * (espessura - parede) / 2)
    caixa(aba, tamanho, parede, lado * (largura + aba) / 2, 0, -espessura / 2)
  }
  const tampa = espessuraDaTampa(largura, espessura)
  for (const [fechar, lado] of [[tampaInicio, -1], [tampaFim, 1]] as const) {
    if (fechar) caixa(largura, tampa, espessura, 0, lado * (tamanho - tampa) / 2, 0)
  }
  // Alma de reforço interna e nervuras transversais, visíveis nas seções.
  caixa(parede, tamanho, espessura - parede * 2, 0, 0, 0)
  for (const y of [-tamanho * 0.3, tamanho * 0.3]) {
    caixa(largura * 0.62, parede * 1.3, parede, 0, y, espessura / 2 + parede / 2)
  }
  const quantidade = Math.max(2, Math.min(14, Math.ceil(tamanho / 0.13)))
  for (const lado of [-1, 1]) for (let i = 0; i < quantidade; i++) {
    chapas.push(new THREE.SphereGeometry(parede * 0.8, 8, 6)
      .scale(1, 1, 0.3)
      .translate(lado * (largura / 2 + aba / 2), (i / (quantidade - 1) - 0.5) * tamanho * 0.88, -espessura / 2 - parede * 0.65))
  }
  // Uma geometria e um material por trecho, inclusive os pontos de solda.
  const resultado = mergeGeometries(chapas)!
  chapas.forEach(chapa => chapa.dispose())
  return resultado
}

export function BarraChapaDetalhada(props: ComponentProps<typeof Barra>) {
  // Tubos da travessa do painel e da suspensão conservam sua seção circular.
  return props.cilindrica ? <Barra {...props} /> : <TrechoChapa {...props} />
}

function TrechoChapa({ id, de, para, largura, espessura, folga = 0, corBase, emendas }: ComponentProps<typeof Barra>) {
  const plano = planejarTrecho(de, para, folga, emendas, new THREE.Quaternion(...orientacaoEntre(de, para)))
  const { tamanho, centro } = plano
  // Numa coluna em polilinha, o extremo sem emenda é livre e leva tampa.
  const tampaInicio = emendas !== undefined && !emendas.inicio
  const tampaFim = emendas !== undefined && !emendas.fim
  const chaveCorte = `${plano.corteInicio?.toArray().join(',') ?? '-'}|${plano.corteFim?.toArray().join(',') ?? '-'}`

  const geometria = useMemo(
    () =>
      cortarExtremos(
        geometriaChapaEstrutural(largura, espessura, tamanho, { tampaInicio, tampaFim }),
        tamanho,
        plano.corteInicio,
        plano.corteFim,
        { faixaDoExtremo: espessuraDaTampa(largura, espessura) },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `chaveCorte` resume os dois cortes
    [largura, espessura, tamanho, tampaInicio, tampaFim, chaveCorte],
  )
  useEffect(() => () => geometria.dispose(), [geometria])
  return <mesh name="chapa-estrutural-detalhada" geometry={geometria}
    position={paraArray(centro)} quaternion={orientacaoEntre(de, para)}>
    <MaterialPeca id={id} corBase={corBase} metalico={0.55} rugosidade={0.42} />
  </mesh>
}
